import { useMemo } from 'react';
import { Spinner } from '@wordpress/components';
import {
	ManagementProvider,
	useManagement,
} from '../context/ManagementContext';
import { BookingCard } from './BookingCard';
import type { Booking } from '../../typings/common/booking';

interface Props {}

interface BookingGroup {
	service: Booking;
	resources: Booking[];
}

function BookingManagement({}: Props) {
	const localizedData = window.gpbBookingManagement;

	if (!localizedData) {
		return (
			<div className="gpb-manage-error">
				Error: Booking data unavailable.
			</div>
		);
	}

	const { key: managementKey, bookings: initialBookings } = localizedData;

	return (
		<ManagementProvider
			managementKey={managementKey}
			bookings={initialBookings}
		>
			<BookingManagementInner />
		</ManagementProvider>
	);
}

function BookingManagementInner() {
	const { bookings, isLoading, error } = useManagement();

	const groupedBookings = useMemo<BookingGroup[]>(() => {
		// Group resources by parent booking ID
		const resourceGroups = new Map<number, Booking[]>();

		bookings.forEach((booking) => {
			if (booking.objectType === 'resource' && booking.parentBookingId) {
				const parentId = booking.parentBookingId;
				if (!resourceGroups.has(parentId)) {
					resourceGroups.set(parentId, []);
				}
				resourceGroups.get(parentId)?.push(booking);
			}
		});

		// Create service bookings from resource groups
		const groups: BookingGroup[] = [];
		resourceGroups.forEach((resources, parentId) => {
			if (resources.length > 0) {
				const firstResource = resources[0];
				// Create a service booking from the resource data
				const serviceBooking: Booking = {
					...firstResource,
					id: parentId,
					objectType: 'service',
					objectTitle: `${firstResource.serviceName || firstResource.objectTitle}`,
					parentBookingId: null,
				};
				groups.push({
					service: serviceBooking,
					resources,
				});
			}
		});

		// Handle standalone service bookings
		bookings.forEach((booking) => {
			if (
				!(booking.objectType === 'resource' && booking.parentBookingId)
			) {
				groups.push({
					service: {
						...booking,
						objectType: 'service',
					},
					resources: [],
				});
			}
		});

		return groups;
	}, [bookings]);

	if (isLoading) {
		return (
			<div className="gpb-manage-loading">
				<Spinner />
			</div>
		);
	}

	if (error) {
		return <div className="gpb-manage-error">{error}</div>;
	}

	return (
		<div className="gpb-manage">
			{groupedBookings.map(({ service, resources }) => (
				<BookingCard
					key={service.id}
					booking={service}
					resources={resources}
				/>
			))}
		</div>
	);
}

export default BookingManagement;
